import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { rootStore } from "stores/RootStore";

interface RetryableRequest extends AxiosRequestConfig {
  _retry?: boolean;
}

/**
 * This will store the Promise of a refresh call when it’s in progress.
 * If null, no refresh call is in progress.
 */
let refreshPromise: Promise<unknown> | null = null;

const api = axios.create({});

api.interceptors.request.use((config) => {
  const { currentUser, tokenType } = rootStore.authStore;

  if (currentUser?.token) {
    config.headers.Authorization = `${tokenType} ${currentUser.token}`;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      const originalRequest = error.config as RetryableRequest;

      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (refreshPromise) {
        await refreshPromise;

        return api(originalRequest);
      }

      refreshPromise = (async () => {
        try {
          // Check if we have a refresh token before attempting refresh
          if (!rootStore.authStore.currentUser?.refreshToken) {
            throw new Error("No refresh token available");
          }

          const { data: refreshResult } = await axios.post(
            process.env.REACT_APP_API_REFRESH_TOKEN || "",
            {
              refreshToken: rootStore.authStore.currentUser.refreshToken,
            },
            {
              headers: {
                // Try with current token (even if expired - backend should accept refresh requests)
                Authorization: `${rootStore.authStore.tokenType} ${rootStore.authStore.currentUser?.token}`,
              },
            }
          );

          // Check if refresh was successful
          if (refreshResult?.type === "success") {
            rootStore.authStore.setCurrentUser(refreshResult.body);
          } else {
            // If refresh result isn't "success", check if it's an auth error
            // Don't logout on temporary backend errors - only logout on actual auth failures
            const errorMessage =
              refreshResult?.error?.body || refreshResult?.error?.title || "";
            const isAuthFailure =
              errorMessage.toLowerCase().includes("token") ||
              errorMessage.toLowerCase().includes("unauthorized") ||
              errorMessage.toLowerCase().includes("expired") ||
              errorMessage.toLowerCase().includes("invalid");

            if (isAuthFailure) {
              throw new Error("Refresh returned an error");
            } else {
              // Temporary backend error - don't logout, just reject the request
              throw new Error("Refresh failed temporarily");
            }
          }
        } catch (refreshError: unknown) {
          // Only logout if it's a real authentication error, not a network error
          // Check if it's a 401/403 (unauthorized) or if refresh token is invalid
          let isAuthError = false;

          if (refreshError instanceof Error) {
            const refreshErr = refreshError as AxiosError | Error;
            const refreshStatus =
              "response" in refreshErr
                ? (refreshErr as AxiosError).response?.status
                : undefined;
            isAuthError =
              refreshStatus === 401 ||
              refreshStatus === 403 ||
              refreshErr.message === "No refresh token available" ||
              refreshErr.message === "Refresh returned an error";

            // Don't logout on temporary failures
            if (refreshErr.message === "Refresh failed temporarily") {
              isAuthError = false;
            }
          }

          if (isAuthError) {
            // If refresh fails due to auth error, logout immediately
            rootStore.authStore.setCurrentUser(null);
            rootStore.authStore.setUserDetails(null);
            rootStore.authStore.setCurrentFmcToken("");
            rootStore.authStore.setBackendFcmToken("");
            rootStore.deliveryPinStore.setDeliveryPin("");

            window.location.href = "/login";
          }
          // For network errors, don't logout - let the request fail and user can retry
          throw refreshError;
        } finally {
          // Refresh call is done
          refreshPromise = null; // Reset so future 403 errors trigger a new refresh
        }
      })();

      // Wait for the refresh to complete or fail
      await refreshPromise;

      // Now retry the original request with the updated token
      return api(originalRequest);
    }

    // If error is not a 403, just reject
    return Promise.reject(error);
  }
);

export default api;
